import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  LibrarySectionSurface,
  PostAnalysisFinalReport,
  type ConfigRow,
  type ReportComment,
  type ReportRerun,
  type ReportSectionComments,
  type ResultRow,
} from '@mightyhive/commoncomponents';
import { apiFetch } from '../../utils/api';

interface LibraryAnalysisTabProps {
  externalUuid: string;
  editAnalysisUrl: string | null;
  editAnalysisLabel?: string | null;
  onEditAnalysis: () => void;
}

interface WorkflowStatus {
  step: string;
  post_analysis_config_id?: number | null;
  post_analysis_results_id?: string | null;
}

interface ReportItemsResponse {
  comments?: ReportComment[];
  rerun?: ReportRerun | null;
}

function analysisBaseUrl(experimentId: string): string {
  return `/api/experiment/geolift/${experimentId}/analysis`;
}

export function LibraryAnalysisTab({
  externalUuid,
  editAnalysisUrl,
  editAnalysisLabel,
  onEditAnalysis,
}: LibraryAnalysisTabProps): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowStep, setWorkflowStep] = useState<string | null>(null);
  const [config, setConfig] = useState<ConfigRow | null>(null);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [comments, setComments] = useState<ReportSectionComments>({});
  const [rerun, setRerun] = useState<ReportRerun | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setConfig(null);
    setResults([]);
    setComments({});
    setRerun(null);

    const base = analysisBaseUrl(externalUuid);

    async function load(): Promise<void> {
      try {
        const statusRes = await apiFetch(`${base}/status`);
        if (!statusRes.ok) throw new Error('status');
        const statusJson = (await statusRes.json()) as { data?: WorkflowStatus };
        const status = statusJson.data;
        if (!active || !status) return;

        setWorkflowStep(status.step);

        if (
          status.step !== 'results' ||
          status.post_analysis_config_id == null ||
          !status.post_analysis_results_id
        ) {
          return;
        }

        const configId = status.post_analysis_config_id;
        const resultsId = status.post_analysis_results_id;

        const [configRes, resultsRes, reportItemsRes] = await Promise.all([
          apiFetch(`${base}/config/${configId}`),
          apiFetch(`${base}/results/${resultsId}`),
          apiFetch(`${base}/config/${configId}/report-items`).catch(() => null),
        ]);

        if (!configRes.ok || !resultsRes.ok) throw new Error('results');

        const configJson = (await configRes.json()) as { data?: { rows?: ConfigRow[] } };
        const resultsJson = (await resultsRes.json()) as { data?: { rows?: ResultRow[] } };

        if (!active) return;
        setConfig(configJson.data?.rows?.[0] ?? null);
        setResults(resultsJson.data?.rows ?? []);

        if (reportItemsRes?.ok) {
          const reportJson = (await reportItemsRes.json()) as { data?: ReportItemsResponse };
          const sectionComments: ReportSectionComments = {};
          (reportJson.data?.comments ?? []).forEach((c) => {
            // Only the persisted (visible) comments are part of the saved final report.
            if (c.is_visible && c.comment.trim() !== '') {
              sectionComments[c.analysis_type] = c.comment;
            }
          });
          if (active) {
            setComments(sectionComments);
            setRerun(reportJson.data?.rerun ?? null);
          }
        }
      } catch {
        if (active) setError('Failed to load analysis data.');
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [externalUuid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (workflowStep !== 'results' || !config || results.length === 0) {
    const notStarted = workflowStep === null || workflowStep === 'upload';
    return (
      <LibrarySectionSurface>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            {notStarted ? 'No analysis performed yet' : 'Analysis in progress'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {notStarted
              ? 'Post-analysis has not been run for this experiment yet. Start the analysis to see the summary here.'
              : 'Post-analysis has started but results are not ready for this read-only view yet.'}
          </Typography>
          {editAnalysisUrl ? (
            <Button variant="contained" onClick={onEditAnalysis} sx={{ textTransform: 'none' }}>
              {editAnalysisLabel ?? (notStarted ? 'Start analysis' : 'Edit analysis')}
            </Button>
          ) : null}
        </Box>
      </LibrarySectionSurface>
    );
  }

  return <PostAnalysisFinalReport config={config} results={results} comments={comments} rerun={rerun} />;
}
